import React, { useEffect, useState } from 'react';
import { fetchAppointments } from './api';

const Dashboard = () => {
  const [appointments, setAppointments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    const loadAppointments = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAppointments();
        setAppointments(data);
      } catch (err) {
        setError('Failed to load appointments.');
      } finally {
        setLoading(false);
      }
    };
    loadAppointments();
  }, []);

  const filteredAppointments = appointments.filter((a) => {
    const matchesSearch =
      a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.service?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.date?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.subject?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    const apptDate = new Date(a.date);
    if (startDate && apptDate < new Date(startDate)) return false;
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      if (apptDate > end) return false;
    }

    return true;
  });

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(
      selectedIds.size === filteredAppointments.length
        ? new Set()
        : new Set(filteredAppointments.map((a) => a.id))
    );
  };

  const downloadSelected = () => {
    if (selectedIds.size === 0) {
      alert('Please select at least one appointment to download.');
      return;
    }

    const headers = ['Patient Name', 'Email', 'Service', 'Phone', 'Date', 'Subject'];
    const rows = filteredAppointments
      .filter((a) => selectedIds.has(a.id))
      .map((a) => [a.name || '', a.email || '', a.service || '', a.phone || '', a.date || '', a.subject || '']);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((val) => `"${val.replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'selected_appointments.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleLogout = () => {
    window.location.href = '/getAppointment/login';
  };

  return (
    <div className="dashboard-container">
      <style>
        {`
          .dashboard-container {
            background: linear-gradient(to right, #e0f7fa, #fce4ec);
            min-height: 100vh;
            padding: 40px 20px;
            display: flex;
            justify-content: center;
            align-items: flex-start;
          }
          .dashboard-card {
            background: #fff;
            border-radius: 15px;
            padding: 30px;
            max-width: 1500px;
            width: 100%;
            box-shadow: 0 10px 25px rgba(0,0,0,0.1);
          }
          .dashboard-title {
            font-weight: bold;
            color: #00796b;
          }
          .table-hover tbody tr:hover {
            background-color: #f1f8e9;
            cursor: pointer;
          }
          .form-control {
            border-radius: 10px;
          }
          .btn {
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 500;
          }
          .btn-outline-danger {
            border-color: #f44336;
            color: #f44336;
          }
          .btn-outline-danger:hover {
            background-color: #f44336;
            color: white;
          }
          .btn-success {
            background-color: #4caf50;
            border-color: #4caf50;
          }
          .btn-success:hover {
            background-color: #43a047;
          }
        `}
      </style>

      <div className="dashboard-card">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="dashboard-title">📅 Appointments Dashboard</h2>
          <div>
            <button className="btn btn-outline-danger me-2" onClick={handleLogout}>
              Logout
            </button>
            <button className="btn btn-success" onClick={downloadSelected}>
              Download Selected
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="row g-3 mb-4 align-items-center">
          <div className="col-md-4">
            <label className="form-label">Search Queries</label>
            <input
              type="text"
              className="form-control"
              placeholder="🔍 Search appointments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">From Date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">To Date</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        {loading && <p className="text-muted">Loading appointments...</p>}
        {error && <p className="text-danger">{error}</p>}

        {/* Table */}
        {!loading && !error && (
          <div className="table-responsive">
            <table className="table table-hover table-bordered align-middle">
              <thead className="table-primary">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        filteredAppointments.length > 0 &&
                        selectedIds.size === filteredAppointments.length
                      }
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>#</th>
                  <th>Patient Name</th>
                  <th>Email</th>
                  <th>Service</th>
                  <th>Phone</th>
                  <th>Date</th>
                  <th>Subject</th>
                </tr>
              </thead>
              <tbody>
                {filteredAppointments.length > 0 ? (
                  filteredAppointments.map((appointment, index) => (
                    <tr key={appointment.id || index}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(appointment.id)}
                          onChange={() => toggleSelect(appointment.id)}
                        />
                      </td>
                      <td>{index + 1}</td>
                      <td>{appointment.name || '-'}</td>
                      <td>{appointment.email || '-'}</td>
                      <td>{appointment.service || '-'}</td>
                      <td>{appointment.phone || '-'}</td>
                      <td>{appointment.date || '-'}</td>
                      <td>{appointment.subject || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">
                      No appointments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
